import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import {
  AdvanceSearchDialog,
  AdvanceSearchDialogProps,
} from "../addMember/addUsers/advanceSearch/AdvanceSearchDialog";
import { userTracking } from "../analytics/userTracking";
import { ConditionalMapsApiProvider } from "../common/ConditionalMapsApiProvider";

export type RestAdvanceSearchDialogProps = Omit<
  AdvanceSearchDialogProps,
  "open"
>;

// Define the shape of the dialog state and open function
const AdvanceSearchDialogContext = createContext({
  openDialog: (_props: RestAdvanceSearchDialogProps, _source?: string) => {},
  closeDialog: () => {},
});

export const useAdvanceSearchDialog = () =>
  useContext(AdvanceSearchDialogContext);

export const AdvanceSearchDialogProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [dialogProps, setDialogProps] =
    useState<RestAdvanceSearchDialogProps | null>(null);
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState<string>("unknown");

  const openDialog = useCallback(
    (props: RestAdvanceSearchDialogProps, source: string = "unknown") => {
      setDialogProps(props);
      setSource(source);
      setOpen(true);

      // Track feature open
      userTracking.trackFeatureOpen("advanced_search", source);
    },
    [],
  );

  const closeDialog = useCallback(() => {
    // Track feature close with duration
    userTracking.trackFeatureClose("advanced_search", source);

    dialogProps?.closeModal?.();
    setOpen(false);
    setDialogProps(null);
  }, [dialogProps, source]);

  return (
    <AdvanceSearchDialogContext.Provider value={{ openDialog, closeDialog }}>
      {children}
      {open && dialogProps && (
        <ConditionalMapsApiProvider>
          <AdvanceSearchDialog
            {...dialogProps}
            open={open}
            closeModal={closeDialog}
          />
        </ConditionalMapsApiProvider>
      )}
    </AdvanceSearchDialogContext.Provider>
  );
};
